import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@/components/Container';
import { Alert, Typography, Button } from '@mui/material';
import TextInput from '@/components/forms/TextInput';
import { BlockComponent } from '@/editor/lib/declarations/EditorComponentTypes';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { DMAutoBlockModel, DMFilterSearchBlockModel, DMViewType } from '@/declarations/models/blocks/DMBlock';
import { DimuOwner } from '@/declarations/models/DimuOwner';
import { useController } from 'react-hook-form';
import { Api } from '@/services/Api';
import Styles from '@/assets/js/Styles';
import { noop } from '@/utils/functions';
import M24PageFinder from '@/components/Finder/M24PageFinder/M24PageFinder';
import PageListManualBlockItem from '@/editor/PageEditor/EditorBlock/PageListBlocks/PageListManualBlockItem';
import { DMSortType, SortDirection } from '@/declarations/models/SortOption';
import FilterKulturNav from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterKulturNav';
import FilterMuseumCollections from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterMuseumCollections';
import FilterObjectTypes from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterObjectTypes';
import FilterLicenseTypes from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterLicenseTypes';
import FilterMaterials from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterMaterials';
// import FilterTechniques from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterTechnique';
import FilterProducers from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterProducers';
import FilterSubjectKeywords from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterSubjectKeywords';
import FilterTime from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterTime';
import FilterPlaces from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterPlaces';
import FilterFreeTextSearch from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterFreeTextSearch';
import FilterGeneralOptions from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/FilterGeneralOptions';
import ResultControls from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/ResultControls';
import { useCurrentBlock } from '../../CurrentBlockProvider';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

const autocompleteItemStyle = (selected: boolean) => ({
  backgroundColor: selected ? 'rgba(0,0,0,0.16) !important' : undefined,
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.08) !important',
  },
});

const getOptionLabel = (option: DimuOwner) => `${option.name} (${option.identifier})`;

export const settingsAreaStyle = {
  width: '100%',
  backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
  padding: 2,
  borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
  gap: 2,
};

export const DMFilterSearchBlock: BlockComponent = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as DMFilterSearchBlockModel;
  const [allDimuOwners, setAllDimuOwners] = useState<Array<DimuOwner>>([]);
  const [selectedOwners, setSelectedOwners] = useState<Array<DimuOwner>>([]);

  const gridSizeOptions = ['auto', '2', '3', '4', '5', '6'];
  currentBlock.view = 'grid' as DMViewType;

  const {
    field: { value: filterPlaces, onChange: setFilterPlaces },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterPlaces`>({
    name: `${blockPath}.userCanFilterPlaces`,
  });

  const {
    field: { value: userCanFilterMaterials, onChange: setUserCanFilterMaterials },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterMaterials`>({
    name: `${blockPath}.userCanFilterMaterials`,
  });

  const {
    field: { value: filterSubjectKeywords, onChange: setFilterSubjectKeywords },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterSubjectKeywords`>({
    name: `${blockPath}.userCanFilterSubjectKeywords`,
  });

  const {
    field: { value: filterLicenseTypes, onChange: setFilterLicenseTypes },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterLicenseTypes`>({
    name: `${blockPath}.userCanFilterLicenseTypes`,
  });

  const {
    field: { value: userCanFilterObjectTypes, onChange: setUserCanFilterObjectTypes },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterObjectTypes`>({
    name: `${blockPath}.userCanFilterObjectTypes`,
  });

  const {
    field: { value: userCanFilterOwnerCodes, onChange: setUserCanFilterOwnerCodes },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterOwnerCodes`>({
    name: `${blockPath}.userCanFilterOwnerCodes`,
  });

  const {
    field: { value: userCanFilterProducers, onChange: setUserCanFilterProducers },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterProducers`>({
    name: `${blockPath}.userCanFilterProducers`,
  });

  const {
    field: { value: userCanFilterTime, onChange: setUserCanFilterTime },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterTime`>({
    name: `${blockPath}.userCanFilterTime`,
  });

  const {
    field: { value: userCanFilterFreeTextSearch, onChange: setUserCanFilterFreeTextSearch },
  } = useController<BlockSpecificPage<DMFilterSearchBlockModel>, `${typeof blockPath}.userCanFilterFreeTextSearch`>({
    name: `${blockPath}.userCanFilterFreeTextSearch`,
  });

  const {
    field: { value: ownerCodes, onChange: setOwnerCodes },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.ownerCodes`>({
    name: `${blockPath}.ownerCodes`,
  });

  const {
    field: { value: maxItems, onChange: setMaxItems },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.maxItems`>({
    name: `${blockPath}.maxItems`,
  });

  const {
    field: { value: paginateMaxItems, onChange: setPaginateMaxItems },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.paginateMaxItems`>({
    name: `${blockPath}.paginateMaxItems`,
  });

  const [pageFinderOpen, setPageFinderOpen] = useState(false);
  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  useEffect(() => {
    const ctx = Api.getDimuOwners();
    const ctx2 = Api.getDimuOwners('sv');

    Promise.all([ctx.fetchDirect(null), ctx2.fetchDirect(null)])
      .then(([res1, res2]) => {
        const allOwners = [...(res1?.owners || []), ...(res2?.owners || [])];
        setAllDimuOwners(allOwners);
        if (ownerCodes) {
          const ownerList = ownerCodes.split(',');
          setSelectedOwners(allOwners.filter((o) => ownerList.some((oc) => oc === o.identifier)));
        }
      })
      .finally(() => {
        ctx.abort();
        ctx2.abort();
      });
  }, [ownerCodes]);

  const handleOwnerCodesChanged = (owners: Array<DimuOwner>) => {
    setSelectedOwners(owners);
    setOwnerCodes(owners.map((o) => o.identifier).join(','));
  };

  const getSortDirectionLabel = (dir: SortDirection) => {
    if (currentBlock?.sortBy === DMSortType.PUBLISHED_AT || currentBlock?.sortBy === DMSortType.UPDATED_AT) {
      return tCommon(`SortDirections.date_${dir}`);
    }
    if (currentBlock?.sortBy === DMSortType.TITLE) return tCommon(`SortDirections.text_${dir}`);
    return tCommon(`SortDirections.${dir}`);
  };

  return (
    <Container gap={2} mt={2} column top left fullWidth>
      <Alert severity='info' sx={{ width: '100%' }}>
        {tComponents('DMFilterSearch.IntroInfo')}
      </Alert>
      <OutlinedContainer
        fullWidth
        left
        label='Ytre begrensninger'
        sx={{
          backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
        }}>
        <FilterKulturNav blockPath={blockPath} tComponents={tComponents} />

        <Container fullWidth column>
          <Container fullWidth left>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
              {tComponents('DMFilterSearch.FolderIdTitleLabel')}
            </Typography>
          </Container>
          <TextInput path={`${blockPath}.folderuuid`} label='UUID' />
        </Container>

        <Container fullWidth column>
          <Container fullWidth left>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
              {tComponents('DMFilterSearch.ExhibitionIdTitleLabel')}
            </Typography>
          </Container>
          <TextInput path={`${blockPath}.exhibitionuuid`} label='UUID' />
        </Container>
      </OutlinedContainer>

      <OutlinedContainer fullWidth column left label='Diverse filtre og innstillinger'>
        <Container fullWidth column gap={2}>
          <FilterMuseumCollections
            blockPath={blockPath}
            tComponents={tComponents}
            allDimuOwners={allDimuOwners}
            selectedOwners={selectedOwners}
            getOptionLabel={getOptionLabel}
            handleOwnerCodesChanged={handleOwnerCodesChanged}
            autocompleteItemStyle={autocompleteItemStyle}
            filterEnabled={userCanFilterOwnerCodes ?? false}
          />
          <FilterObjectTypes
            blockPath={blockPath}
            currentBlock={currentBlock}
            tComponents={tComponents}
            tCommon={tCommon}
            filterEnabled={userCanFilterObjectTypes ?? false}
          />
          <FilterLicenseTypes
            blockPath={blockPath}
            currentBlock={currentBlock}
            tComponents={tComponents}
            tCommon={tCommon}
            filterEnabled={filterLicenseTypes ?? false}
          />
          <FilterSubjectKeywords
            blockPath={blockPath}
            tComponents={tComponents}
            filterEnabled={filterSubjectKeywords ?? false}
          />
          <FilterMaterials
            blockPath={blockPath}
            tComponents={tComponents}
            filterEnabled={userCanFilterMaterials ?? false}
          />
          {/* <FilterTechniques blockPath={blockPath} tComponents={tComponents} /> */}
          <FilterPlaces blockPath={blockPath} tComponents={tComponents} filterEnabled={filterPlaces ?? false} />
          <FilterProducers
            blockPath={blockPath}
            tComponents={tComponents}
            filterEnabled={userCanFilterProducers ?? false}
          />
          <FilterTime blockPath={blockPath} tComponents={tComponents} filterEnabled={userCanFilterTime ?? false} />
          <FilterFreeTextSearch
            blockPath={blockPath}
            tComponents={tComponents}
            filterEnabled={userCanFilterFreeTextSearch ?? false}
          />
        </Container>
      </OutlinedContainer>
      <ResultControls
        blockPath={blockPath}
        currentBlock={currentBlock}
        getSortDirectionLabel={getSortDirectionLabel}
        maxItems={maxItems}
        setMaxItems={setMaxItems}
        paginateMaxItems={paginateMaxItems}
        setPaginateMaxItems={setPaginateMaxItems}
        gridSizeOptions={gridSizeOptions}
        tCommon={tCommon}
        tComponents={tComponents}
      />
      <OutlinedContainer fullWidth column left label={tComponents('DMBlock.DmLinkLabel')}>
        <Container fullWidth left pt={1} gap={2}>
          <TextInput path={`${blockPath}.dmLink`} defaultValue='' label={tComponents('DMBlock.DmLink')} size='small' />
          <TextInput
            path={`${blockPath}.dmLinkText`}
            defaultValue=''
            label={tComponents('DMBlock.DmLinkText')}
            size='small'
          />
        </Container>
      </OutlinedContainer>
      <M24PageFinder
        hideSiteSelector
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />
      <OutlinedContainer fullWidth column left label={tComponents('EventsBlock.ShowAllLinkPageTitle')}>
        {showAllLinkPage && (
          <PageListManualBlockItem
            index={0}
            onReorder={noop}
            onDelete={() => onChangeShowAllLinkPage(null)}
            overrideItemPath={`${blockPath}.showAllLinkPage`}
            color={Styles.Colors.LIGHT_GREY}
            hideHighlightCheckbox
            hideSkinSelector
            disableReordering
          />
        )}
        <Button onClick={() => setPageFinderOpen(true)} variant='outlined' color='secondary'>
          {tComponents('EventsBlock.ShowAllLinkPageButtonLabel')}
        </Button>
      </OutlinedContainer>
    </Container>
  );
};
